import { Badge, Box, IconButton, Stack, ToggleButtonGroup, Tooltip, Divider } from '@mui/joy';
import { type ReactElement, useState } from 'react';
import {
  usePortfolioDefDashboardInputSuspenseQuery,
  useGetPortfolioDefTagsSuspenseQuery,
} from '../../../../generated/graphql.tsx';
import { defaultRowSpacing } from '../../../StackSpacing.ts';
import GFab from 'components/technical/inputs/GFab.tsx';
import { ScienceOutlined } from '@mui/icons-material';
import { motion } from 'framer-motion';
import { pageInsetSpacing, transitionDurationS } from '../../../../theme/consts.ts';
import { useNavigate } from 'react-router';
import HeaderBar from 'components/technical/HeaderBar/HeaderBar.tsx';
import { Gear } from 'components/technical/icons/index.tsx';
import GLink from 'components/technical/GLink/GLink.tsx';
import GridViewIcon from '@mui/icons-material/GridView';
import ReorderIcon from '@mui/icons-material/Reorder';
import { PortfolioDefCards } from './PortfolioDefCards.tsx';
import { PortfolioDefGrid } from './PortfolioDefGrid.tsx';

const PortfolioDefsDashboard = (): ReactElement => {
  const [value, setValue] = useState<'list' | 'card'>('card');
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const navigate = useNavigate();
  const { data } = usePortfolioDefDashboardInputSuspenseQuery();
  const { data: tagsData } = useGetPortfolioDefTagsSuspenseQuery();
  const { definitions, accounts, subFunds } = data.portfolio;
  const allTags = tagsData.portfolio.portfolioDefTags ?? [];

  return (
    <Stack
      direction="column"
      rowGap={defaultRowSpacing}
      minHeight={'100%'}
      sx={{ height: '100%' /* for min height on grid to work */ }}
    >
      <HeaderBar level="h2" title="Portfolio lab" hideBottomPadding actionsGap={1}>
        <ToggleButtonGroup value={value} onChange={(_event, newValue) => setValue(newValue as 'list' | 'card')}>
          <IconButton value="list">
            <Tooltip title="List view">
              <ReorderIcon />
            </Tooltip>
          </IconButton>
          <IconButton value="card">
            <Tooltip title="Card view">
              <GridViewIcon />
            </Tooltip>
          </IconButton>
        </ToggleButtonGroup>
        <Divider orientation="vertical" sx={{ mx: 1 }} />
        <GLink to="/app/copilot/lab/rebalacing-rule-templates">
          {/* the icons uses plain variant so visually it seems farther from the divider. We need to shift it a bit right, 
            with the margin depending on the particular icon */}
          <IconButton variant="plain" sx={{ ml: -1 }}>
            <Tooltip title="Configure rebalacing rule templates">
              <Gear color="inherit" fontSize="xl2" />
            </Tooltip>
          </IconButton>
        </GLink>
      </HeaderBar>
      {value === 'list' ? (
        <PortfolioDefGrid
          defs={definitions}
          selectedIds={selectedIds}
          setSelectedIds={setSelectedIds}
          accounts={accounts}
          subFunds={subFunds.list}
          allTags={allTags}
        />
      ) : (
        <PortfolioDefCards
          defs={definitions}
          accounts={accounts}
          subFunds={subFunds}
          selectedIds={selectedIds}
          setSelectedIds={setSelectedIds}
          allTags={allTags}
        />
      )}
      {selectedIds.length > 0 && (
        <Box
          sx={(theme) => ({
            position: 'fixed',
            bottom: theme.spacing(pageInsetSpacing),
            right: theme.spacing(pageInsetSpacing),
          })}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: transitionDurationS.Short }}
          >
            <Badge
              size="md"
              color="primary"
              badgeInset="8%"
              badgeContent={selectedIds.length}
              invisible={selectedIds.length === 0}
            >
              <GFab
                startDecorator={<ScienceOutlined />}
                onClick={() => {
                  navigate('/app/copilot/lab/portfolio/backtesting', {
                    state: {
                      portfolioIds: selectedIds,
                    },
                  });
                }}
              >
                Analyze
              </GFab>
            </Badge>
          </motion.div>
        </Box>
      )}
    </Stack>
  );
};

export default PortfolioDefsDashboard;
